/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/invoiceRequests.scss';

import {
    AwesomeIcon,
    ChangeFunction,
    Checkbox,
    copyToClipboard,
    Loader,
    MenuPlacement,
    OptionsMenu,
    OptionsMenuItem,
    SelectItemShape,
    stopPropagation
} from '@dvrd/dvr-controls';

import React, {KeyboardEventHandler, MouseEventHandler, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {
    AccessKey,
    CustomerType,
    InvoiceRequestsTab,
    InvoiceRequestStatus,
    InvoiceUploadStats,
    InvoiceUploadStatus
} from '../../../util/interfaces';

import BolInvoiceRequest from '../../../models/bolInvoiceRequestModel';

import {BluemateButton, BluemateInput, BluemateSelect, BluemateTabs} from '../../controls';

import classNames from 'classnames';

import CountryFlag from '../../misc/countryFlag';

import optionsHorizontal from '@iconify/icons-mi/options-horizontal';

import {Icon} from '@iconify/react';

import {AppContext} from '../../misc/appContext';

import InvoiceUploadSettingsController from '../../invoiceUploadSettings/invoiceUploadSettingsController';

import {hasAdminLogin} from '../../../util/adminUtil';

import {userHasRight} from '../../../util/accessUtil';

import useRight from '../../misc/useRight';

import {showSnackbar} from '@dvrd/dvr-controls';

interface Props {
    onChangeSearch: ChangeFunction<string>;
    onChangePage: (page: number) => MouseEventHandler;
    onChangeStatus: (status: InvoiceUploadStatus | null) => MouseEventHandler;
    onChangeLimit: ChangeFunction<number>;
    onSelectRequest: (invoiceId: string) => ChangeFunction<boolean>;
    onEnterSearch: KeyboardEventHandler;
    onUploadInvoice: (invoiceRequest: BolInvoiceRequest) => MouseEventHandler;
    onEditInvoice: (invoiceId: string) => MouseEventHandler;
    onPreviewInvoice: (invoiceId: string) => MouseEventHandler;
    onDownloadInvoice: (invoiceId: string) => MouseEventHandler;
    onUploadMultipleInvoices: MouseEventHandler;
    onSelectAll: ChangeFunction;
    onChangeTab: (tab: InvoiceRequestsTab) => void;
    onArchive: (invoiceRequest: BolInvoiceRequest) => MouseEventHandler;
    onFetchOrder: (invoiceRequest: BolInvoiceRequest) => MouseEventHandler;
    invoiceRequests: Array<BolInvoiceRequest>;
    total: number;
    loading: boolean;
    search: string;
    page: number;
    status: InvoiceUploadStatus;
    limit: number;
    selectedInvoices: Set<string>;
    stats: InvoiceUploadStats;
    allSelected: boolean;
    tab: InvoiceRequestsTab;
}

const limitItems: SelectItemShape[] = [
    {label: '10', value: 10},
    {label: '20', value: 20},
    {label: '50', value: 50}
];

export default function InvoiceRequests(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const [canDownloadInvoice, canEditCustomer, canEditItems, canArchive, canUpload] = useRight(
        AccessKey.INVOICE_DOWNLOAD,
        AccessKey.INVOICE_EDIT_CUSTOMER,
        AccessKey.INVOICE_EDIT_ITEMS,
        AccessKey.INVOICE_REQUEST_ARCHIVE,
        AccessKey.INVOICE_REQUEST_UPLOAD
    );
    const {
        invoiceRequests,
        status,
        onChangeStatus,
        page,
        onChangeLimit,
        limit,
        onChangeSearch,
        search,
        onChangePage,
        total,
        loading,
        selectedInvoices,
        onSelectRequest,
        onEnterSearch,
        stats,
        onPreviewInvoice,
        onUploadInvoice,
        onEditInvoice,
        onDownloadInvoice,
        onUploadMultipleInvoices,
        onSelectAll,
        allSelected,
        tab,
        onChangeTab,
        onArchive,
        onFetchOrder
    } = props;
    const allDisabled = !invoiceRequests.find((request: BolInvoiceRequest) => request.isRetryable());

    function getInvoiceMenuItems(invoiceRequest: BolInvoiceRequest): Array<OptionsMenuItem> {
        const {invoiceId} = invoiceRequest;
        const options: Array<OptionsMenuItem> = [];
        if (invoiceId.length) {
            if (canDownloadInvoice)
                options.push(
                    {
                        label: t('invoice.request.preview', 'Voorbeeld'),
                        icon: <AwesomeIcon name='eye' className='icon' />,
                        onClick: onPreviewInvoice(invoiceId)
                    },
                    {
                        label: t('invoice.request.download', 'Downloaden'),
                        icon: <AwesomeIcon name='download' className='icon' />,
                        onClick: onDownloadInvoice(invoiceId)
                    }
                );
            if (canEditCustomer || canEditItems)
                options.push({
                    label: t('invoice.request.edit', 'Wijzigen...'),
                    icon: <AwesomeIcon name='pen' className='icon' />,
                    onClick: onEditInvoice(invoiceId)
                });
        } else {
            options.push({
                label: t('invoice.request.createInvoice', 'Factuur aanmaken...'),
                icon: <AwesomeIcon name='download' className='icon' />,
                onClick: onFetchOrder(invoiceRequest)
            });
            if (canArchive)
                options.push({
                    label: t('invoice.request.archive', 'Archiveren'),
                    icon: <AwesomeIcon name='box-archive' className='icon' />,
                    onClick: onArchive(invoiceRequest)
                });
        }
        const isAdmin = hasAdminLogin();
        if (invoiceRequest.isRetryable(isAdmin) && canUpload)
            options.push({
                label: t('invoice.request.upload', 'Uploaden'),
                icon: <AwesomeIcon name='upload' className='icon' />,
                onClick: onUploadInvoice(invoiceRequest)
            });
        return options;
    }

    const invoiceTabs = useMemo(() => {
        const tabs = [{key: InvoiceRequestsTab.REQUESTS, label: t('invoice.request.tab.requests', 'Aanvragen')}];
        if (userHasRight(context.accountContext.account, AccessKey.INVOICE_REQUEST_SETTINGS))
            tabs.push({key: InvoiceRequestsTab.SETTINGS, label: t('invoice.request.tab.settings', 'Instellingen')});
        return tabs;
    }, [context.accountContext.account, t]);

    function renderTabs() {
        return (
            <BluemateTabs<InvoiceRequestsTab>
                tabs={invoiceTabs}
                activeTab={tab}
                onChangeTab={onChangeTab}
                className='tabs-container'
            />
        );
    }

    function renderRequestsTab() {
        return (
            <>
                {renderFilterBar()}
                {renderRequests()}
                {renderPagination()}
            </>
        );
    }

    function renderFilterBar() {
        const selectedLabel = allSelected
            ? t('invoice.request.allSelected', 'Alle aanvragen geselecteerd')
            : selectedInvoices.size
              ? `${selectedInvoices.size} ${selectedInvoices.size === 1 ? t('invoice.request.requestSingular', 'aanvraag') : t('invoice.request.requestPlural', 'aanvragen')} ${t('invoice.request.selected', 'geselecteerd')}`
              : null;
        const isAdmin = hasAdminLogin();
        const _canUpload = isAdmin || canUpload;
        return (
            <div className='filter-bar'>
                <BluemateInput
                    onChange={onChangeSearch}
                    value={search}
                    onEnter={onEnterSearch}
                    label={t('invoice.request.searchLabel', 'Aanvraag zoeken')}
                    className='search-field'
                    inputProps={{
                        placeholder: t('invoice.request.searchPlaceholder', 'Ordernummer, verzendnummer of klantnummer')
                    }}
                />
                <div className='action-bar'>
                    <div className={classNames('status-buttons', isAdmin && 'admin')}>
                        {renderStatusButton(InvoiceUploadStatus.OPEN)}
                        {renderStatusButton(InvoiceUploadStatus.CLOSED)}
                        {isAdmin && renderStatusButton(InvoiceUploadStatus.FAILED)}
                    </div>
                    <div
                        className={classNames(
                            'invoice-upload-container',
                            status === InvoiceUploadStatus.CLOSED && 'hidden'
                        )}
                    >
                        <label className='selected-label'>{selectedLabel}</label>
                        <BluemateButton
                            onClick={onUploadMultipleInvoices}
                            label={t('invoice.request.upload', 'Uploaden')}
                            disabled={loading || !total || allDisabled || !_canUpload}
                        />
                    </div>
                </div>
            </div>
        );
    }

    function renderStatusButton(_status: InvoiceUploadStatus) {
        let label: string;
        if (_status === InvoiceUploadStatus.OPEN)
            label = `${t('invoice.request.status.open', 'Open')} (${stats.open === -1 ? '-' : stats.open})`;
        else if (_status === InvoiceUploadStatus.FAILED)
            label = `${t('invoice.request.status.failed', 'Mislukt')} (${stats.failed})`;
        else label = `${t('invoice.request.status.closed', 'Gesloten')} (${stats.closed.total})`;
        return (
            <div
                className={classNames('status-button', _status === status && 'active')}
                onClick={onChangeStatus(_status)}
            >
                <label className='button-label'>{label}</label>
            </div>
        );
    }

    function renderRequests() {
        return (
            <div className='requests-container'>
                <Loader active={loading} />
                <div
                    className={classNames(
                        'row default-table-row head',
                        status === InvoiceUploadStatus.CLOSED && 'closed'
                    )}
                >
                    <Checkbox
                        checked={allSelected}
                        onCheck={onSelectAll}
                        disabled={loading || allDisabled}
                        className={classNames(status === InvoiceUploadStatus.CLOSED && 'hidden')}
                    />
                    <label>{t('invoice.request.col.requestDate', 'AANVRAAGDATUM')}</label>
                    {status === InvoiceUploadStatus.CLOSED && (
                        <label>{t('invoice.request.col.uploadedOn', 'GEÜPLOAD OP')}</label>
                    )}
                    <label>{t('invoice.request.col.orderNumber', 'ORDERNUMMER')}</label>
                    <label>{t('invoice.request.col.type', 'TYPE')}</label>
                    <label>{t('invoice.request.col.status', 'STATUS')}</label>
                    <div />
                </div>
                {invoiceRequests.map(renderRequest)}
                {!invoiceRequests.length && (
                    <div className='default-table-row empty'>
                        <label className='empty-label'>
                            {t('invoice.request.empty', '- Geen factuuraanvragen gevonden -')}
                        </label>
                    </div>
                )}
            </div>
        );
    }

    function renderRequest(invoiceRequest: BolInvoiceRequest, index: number) {
        const selected =
            invoiceRequest.isRetryable() && (allSelected || selectedInvoices.has(invoiceRequest.invoiceId));
        let optionsMenu: OptionsMenu;

        function onClickRow() {
            optionsMenu.onOpenExternal();
        }

        const customerType: CustomerType | null = invoiceRequest.invoice?.customerType ?? null;
        let customerTypeLabel: string;
        if (customerType === null || customerType === CustomerType.BOTH) customerTypeLabel = '-';
        else if (customerType === CustomerType.BUSINESS)
            customerTypeLabel = t('invoice.request.customerType.business', 'Zakelijk');
        else customerTypeLabel = t('invoice.request.customerType.private', 'Particulier');
        return (
            <div
                key={index}
                className={classNames('row default-table-row', status === InvoiceUploadStatus.CLOSED && 'closed')}
                onClick={onClickRow}
            >
                <Checkbox
                    checked={selected}
                    onCheck={onSelectRequest(invoiceRequest.invoiceId)}
                    disabled={loading || !invoiceRequest.isRetryable()}
                    className={classNames(status === InvoiceUploadStatus.CLOSED && 'hidden')}
                />
                <label>{invoiceRequest.requestDate?.format('DD-MM-YYYY HH:mm') ?? '-'}</label>
                {status === InvoiceUploadStatus.CLOSED && (
                    <label>{invoiceRequest.uploadedAt?.format('DD-MM-YYYY HH:mm') ?? '-'}</label>
                )}
                {renderCountryCode(invoiceRequest)}
                <label>{customerTypeLabel}</label>
                <label>{invoiceRequest.translatedStatus}</label>
                <div className='invoice-request-actions'>
                    {renderActionButton(invoiceRequest)}
                    <OptionsMenu
                        options={getInvoiceMenuItems(invoiceRequest)}
                        placement={MenuPlacement.BOTTOM_LEFT}
                        optionsComponent={<Icon icon={optionsHorizontal} className='options-icon' />}
                        containerClass='common-options'
                        ref={(ref: OptionsMenu) => {
                            optionsMenu = ref;
                        }}
                    />
                </div>
            </div>
        );
    }

    function renderCountryCode(invoiceRequest: BolInvoiceRequest) {
        function onCopy(evt: React.MouseEvent) {
            if (invoiceRequest.invoice) return;
            stopPropagation(evt);
            copyToClipboard(invoiceRequest.orderId).then(() => {
                showSnackbar(
                    `${t('invoice.request.orderNumber', 'Ordernummer')} '${invoiceRequest.orderId}' ${t(
                        'invoice.request.copiedToClipboard',
                        'gekopieerd'
                    )}`
                );
            });
        }

        return (
            <label className='order-id' onClick={onCopy}>
                {!!invoiceRequest.countryCode && <CountryFlag countryCode={invoiceRequest.countryCode} />}{' '}
                {invoiceRequest.orderId} {!invoiceRequest.invoice && <AwesomeIcon name='copy' className='btn-copy' />}
            </label>
        );
    }

    function renderActionButton(invoiceRequest: BolInvoiceRequest) {
        let btnText: string;
        let btnAction: MouseEventHandler;
        if (invoiceRequest.isRetryable()) {
            btnText = t('invoice.request.upload', 'Uploaden');
            btnAction = onUploadInvoice(invoiceRequest);
        } else if (invoiceRequest.status === InvoiceRequestStatus.INVOICE_REQUESTED) {
            btnText = t('invoice.request.createInvoice', 'Factuur aanmaken...');
            btnAction = onFetchOrder(invoiceRequest);
        } else return null;
        return <BluemateButton onClick={btnAction} label={btnText} className='upload-btn' disabled={loading} />;
    }

    function renderPagination() {
        if (status === InvoiceUploadStatus.OPEN) return null;
        const maxPage = Math.ceil(total / limit);
        const prevDisabled = page === 0;
        const nextDisabled = page >= maxPage - 1;
        const from = Math.min(total, page * limit + 1);
        const to = Math.min(total, (page + 1) * limit);
        return (
            <div className='pagination'>
                <label className='display-count'>
                    {from} {t('invoice.request.paginationTo', 't/m')} {to} {t('invoice.request.paginationOf', 'van')}{' '}
                    {total}
                </label>
                <AwesomeIcon
                    name='chevron-left'
                    className={classNames('pager', prevDisabled && 'disabled')}
                    onClick={prevDisabled ? undefined : onChangePage(page - 1)}
                />
                <AwesomeIcon
                    name='chevron-right'
                    className={classNames('pager', nextDisabled && 'disabled')}
                    onClick={nextDisabled ? undefined : onChangePage(page + 1)}
                />
                <BluemateSelect onChange={onChangeLimit} value={limit} items={limitItems} itemsPosition='top' />
            </div>
        );
    }

    function renderSettings() {
        return <InvoiceUploadSettingsController />;
    }

    return (
        <div className='invoice-requests'>
            <label className='col-title'>{t('invoice.request.title', 'Factuuraanvragen')}</label>
            {renderTabs()}
            {tab === InvoiceRequestsTab.REQUESTS && renderRequestsTab()}
            {tab === InvoiceRequestsTab.SETTINGS && renderSettings()}
        </div>
    );
}
